import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useMasterData } from "@/hooks/useMasterData";

const EMPTY = {
  // Personal
  first_name: "", last_name: "", email: "", phone: "", whatsapp: "",
  date_of_birth: "", gender: "", marital_status: "", nationality: "", ssn_last4: "",
  languages: [],
  // Location
  country: "", current_city: "",
  // Enrollment / service
  service_purchased: "", service_type: "", program_duration_days: 90,
  service_start_date: new Date().toISOString().slice(0, 10),
  enrollment_date: "", deadline_date: "",
  // Professional
  experience: "", total_experience_years: "", relevant_experience_years: "",
  education: "", university: "", graduation_year: "",
  current_employer: "", portfolio_url: "", linkedin: "", resume_version: "",
  certifications: [], skills: [], technologies: [],
  // Visa
  visa_status: "", visa_type: "", visa_start: "", visa_end: "",
  sponsorship_required: false,
  // Preferences
  preferred_role: "", preferred_location: "",
  work_preferences: [], preferred_industries: [], preferred_states: [],
  preferred_job_titles: [], preferred_companies: [], preferred_keywords: [],
  willing_to_relocate: false,
  // Salary
  current_salary: "", salary_expectation_min: "", salary_expectation_max: "",
  salary_currency: "USD",
  // Marketing
  marketing_email: "", marketing_type: "", placement_field: "", source_of_hire: "",
  // Meta
  priority: "Normal", evaluation_notes: "",
};

// Parse comma-separated to list, and coerce numeric-ish blanks to null.
function pack(form) {
  const out = { ...form };
  // Blank number fields -> undefined so backend keeps them null
  const numericKeys = [
    "total_experience_years", "relevant_experience_years", "graduation_year",
    "current_salary", "salary_expectation_min", "salary_expectation_max",
    "program_duration_days",
  ];
  numericKeys.forEach((k) => {
    if (out[k] === "" || out[k] == null) out[k] = null;
    else out[k] = Number(out[k]);
  });
  // Blank date/text -> null
  ["date_of_birth", "enrollment_date", "deadline_date", "visa_start", "visa_end"].forEach((k) => {
    if (!out[k]) out[k] = null;
  });
  // Remove empty lists that are strings (defensive)
  ["languages", "certifications", "skills", "technologies",
   "work_preferences", "preferred_industries", "preferred_states",
   "preferred_job_titles", "preferred_companies", "preferred_keywords"].forEach((k) => {
    if (!Array.isArray(out[k])) out[k] = [];
  });
  return out;
}

function Section({ title, children }) {
  return (
    <fieldset className="border border-slate-200 rounded-md p-4 space-y-3">
      <legend className="text-xs uppercase tracking-widest text-slate-500 px-2">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </fieldset>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, required, testId, type = "text", ...rest }) {
  return (
    <input
      type={type}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
      {...rest}
    />
  );
}

function Select({ value, onChange, options, testId, placeholder = "— select —" }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.id || o.value || o} value={o.value || o}>{o.value || o}</option>)}
    </select>
  );
}

function MultiSelectChips({ value = [], options, onChange, testId, placeholder = "select…" }) {
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div className="border border-slate-200 rounded-md px-2 py-2 text-sm space-y-1" data-testid={testId}>
      <div className="flex flex-wrap gap-1">
        {value.length === 0 && <span className="text-slate-400 text-xs">{placeholder}</span>}
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 text-xs rounded-full px-2 py-0.5">
            {v}
            <button type="button" onClick={() => toggle(v)} className="hover:text-red-600">×</button>
          </span>
        ))}
      </div>
      <select
        value=""
        onChange={(e) => { if (e.target.value) toggle(e.target.value); }}
        className="w-full border-0 focus:outline-none text-xs text-slate-500 bg-transparent"
      >
        <option value="">+ add…</option>
        {options.map((o) => {
          const v = o.value || o;
          return !value.includes(v) ? <option key={o.id || v} value={v}>{v}</option> : null;
        })}
      </select>
    </div>
  );
}

export default function CandidateCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // UAT Batch 1 — Service Types master (drives Service Type dropdown + auto-fills program_duration_days).
  const [serviceTypes, setServiceTypes] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/masters/service-types?page_size=100");
        setServiceTypes(r.data.rows || []);
      } catch { setServiceTypes([]); }
    })();
  }, []);
  // Whenever the Service Type changes, auto-fill program_duration_days from the master's default.
  useEffect(() => {
    if (!form.service_type) return;
    const st = serviceTypes.find((s) => s.name === form.service_type);
    if (st?.default_duration_days) {
      setForm((f) => ({ ...f, program_duration_days: st.default_duration_days }));
    }
  }, [form.service_type, serviceTypes]);
  // Auto-compute deadline_date = service_start_date + program_duration_days.
  useEffect(() => {
    if (!form.service_start_date || !form.program_duration_days) return;
    try {
      const start = new Date(form.service_start_date);
      const d = new Date(start.getTime() + Number(form.program_duration_days) * 86400000);
      const iso = d.toISOString().slice(0, 10);
      setForm((f) => ({ ...f, deadline_date: iso }));
    } catch { /* ignore */ }
  }, [form.service_start_date, form.program_duration_days]);

  // Master data feeds
  const md = {
    visa_status: useMasterData("visa_status").items,
    work_preference: useMasterData("work_preference").items,
    marketing_type: useMasterData("marketing_type").items,
    placement_field: useMasterData("placement_field").items,
    technologies: useMasterData("technologies").items,
    roles: useMasterData("roles").items,
    gender: useMasterData("gender").items,
    marital_status: useMasterData("marital_status").items,
    salary_currency: useMasterData("salary_currency").items,
    source_of_hire: useMasterData("source_of_hire").items,
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/candidates", pack(form));
      toast.success("Candidate created");
      onCreated?.();
      onClose();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50" data-testid="candidate-modal">
      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-4xl max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add Candidate</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">×</button>
        </div>

        <Section title="Personal">
          <Field label="First Name *"><TextInput required testId="cand-input-first_name" value={form.first_name} onChange={set("first_name")} /></Field>
          <Field label="Last Name *"><TextInput required testId="cand-input-last_name" value={form.last_name} onChange={set("last_name")} /></Field>
          <Field label="Email *"><TextInput required type="email" testId="cand-input-email" value={form.email} onChange={set("email")} /></Field>
          <Field label="Phone"><TextInput testId="cand-input-phone" value={form.phone} onChange={set("phone")} /></Field>
          <Field label="WhatsApp"><TextInput testId="cand-input-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} /></Field>
          <Field label="Date of Birth"><TextInput type="date" testId="cand-input-dob" value={form.date_of_birth} onChange={set("date_of_birth")} /></Field>
          <Field label="Gender"><Select value={form.gender} onChange={set("gender")} options={md.gender} testId="cand-input-gender" /></Field>
          <Field label="Marital Status"><Select value={form.marital_status} onChange={set("marital_status")} options={md.marital_status} /></Field>
          <Field label="Nationality"><TextInput value={form.nationality} onChange={set("nationality")} /></Field>
          <Field label="Country"><TextInput value={form.country} onChange={set("country")} /></Field>
          <Field label="Current City"><TextInput value={form.current_city} onChange={set("current_city")} /></Field>
          <Field label="SSN (last 4 digits only)">
            <TextInput
              testId="cand-input-ssn"
              placeholder="1234"
              maxLength={4}
              value={form.ssn_last4}
              onChange={(v) => set("ssn_last4")(v.replace(/\D/g, "").slice(-4))}
            />
          </Field>
        </Section>

        <Section title="Enrollment & Service">
          <Field label="Service Purchased"><TextInput value={form.service_purchased} onChange={set("service_purchased")} /></Field>
          <Field label="Service Type">
            <Select testId="cand-input-service_type" value={form.service_type} onChange={set("service_type")} options={serviceTypes.map((s) => s.name)} />
          </Field>
          <Field label="Program Duration (days) *auto-computed from Service Type"><TextInput type="number" value={form.program_duration_days} onChange={set("program_duration_days")} /></Field>
          <Field label="Enrollment Date"><TextInput type="date" testId="cand-input-enrollment" value={form.enrollment_date} onChange={set("enrollment_date")} /></Field>
          <Field label="Service Start Date"><TextInput type="date" value={form.service_start_date} onChange={set("service_start_date")} /></Field>
          <Field label="Deadline Date (auto)"><TextInput type="date" testId="cand-input-deadline" value={form.deadline_date} onChange={set("deadline_date")} /></Field>
          <Field label="Priority">
            <select value={form.priority} onChange={(e) => set("priority")(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option>Normal</option><option>Hot</option><option>Urgent</option>
            </select>
          </Field>
        </Section>

        <Section title="Professional">
          <Field label="Preferred Role"><Select value={form.preferred_role} onChange={set("preferred_role")} options={md.roles} testId="cand-input-preferred_role" /></Field>
          <Field label="Current Employer"><TextInput value={form.current_employer} onChange={set("current_employer")} /></Field>
          <Field label="Total Experience (years)"><TextInput type="number" step="0.1" testId="cand-input-total_exp" value={form.total_experience_years} onChange={set("total_experience_years")} /></Field>
          <Field label="Relevant Experience (years)"><TextInput type="number" step="0.1" value={form.relevant_experience_years} onChange={set("relevant_experience_years")} /></Field>
          <Field label="Education (highest)"><TextInput testId="cand-input-education" value={form.education} onChange={set("education")} /></Field>
          <Field label="University"><TextInput value={form.university} onChange={set("university")} /></Field>
          <Field label="Graduation Year"><TextInput type="number" value={form.graduation_year} onChange={set("graduation_year")} /></Field>
          <Field label="LinkedIn"><TextInput value={form.linkedin} onChange={set("linkedin")} /></Field>
          <Field label="Portfolio URL"><TextInput value={form.portfolio_url} onChange={set("portfolio_url")} /></Field>
          <Field label="Resume Version"><TextInput value={form.resume_version} onChange={set("resume_version")} /></Field>
          <Field label="Skills">
            <MultiSelectChips testId="cand-input-skills" value={form.skills} onChange={set("skills")} options={md.technologies} placeholder="add skills…" />
          </Field>
          <Field label="Technologies">
            <MultiSelectChips value={form.technologies} onChange={set("technologies")} options={md.technologies} placeholder="add technologies…" />
          </Field>
        </Section>

        <Section title="Visa">
          <Field label="Visa Status">
            <Select testId="cand-input-visa_status" value={form.visa_status} onChange={set("visa_status")} options={md.visa_status} />
          </Field>
          <Field label="Visa Type (details)"><TextInput value={form.visa_type} onChange={set("visa_type")} /></Field>
          <Field label="Visa Start Date"><TextInput type="date" value={form.visa_start} onChange={set("visa_start")} /></Field>
          <Field label="Visa End Date"><TextInput type="date" value={form.visa_end} onChange={set("visa_end")} /></Field>
          <Field label="Sponsorship Required">
            <label className="inline-flex items-center gap-2 text-sm mt-2" data-testid="cand-input-sponsorship">
              <input type="checkbox" checked={!!form.sponsorship_required}
                     onChange={(e) => set("sponsorship_required")(e.target.checked)} />
              Requires sponsorship
            </label>
          </Field>
        </Section>

        <Section title="Preferences">
          <Field label="Preferred Job Location"><TextInput value={form.preferred_location} onChange={set("preferred_location")} /></Field>
          <Field label="Work Preferences">
            <MultiSelectChips testId="cand-input-work_prefs" value={form.work_preferences} onChange={set("work_preferences")} options={md.work_preference} placeholder="remote / hybrid / onsite…" />
          </Field>
          <Field label="Willing to Relocate">
            <label className="inline-flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={!!form.willing_to_relocate}
                     onChange={(e) => set("willing_to_relocate")(e.target.checked)} />
              Yes
            </label>
          </Field>
        </Section>

        <Section title="Salary">
          <Field label="Current Salary"><TextInput type="number" value={form.current_salary} onChange={set("current_salary")} /></Field>
          <Field label="Expected — Min"><TextInput type="number" testId="cand-input-salary_min" value={form.salary_expectation_min} onChange={set("salary_expectation_min")} /></Field>
          <Field label="Expected — Max"><TextInput type="number" testId="cand-input-salary_max" value={form.salary_expectation_max} onChange={set("salary_expectation_max")} /></Field>
          <Field label="Currency">
            <Select value={form.salary_currency} onChange={set("salary_currency")} options={md.salary_currency} placeholder="USD" />
          </Field>
        </Section>

        <Section title="Marketing / Assignment">
          <Field label="Marketing Email"><TextInput testId="cand-input-marketing_email" type="email" value={form.marketing_email} onChange={set("marketing_email")} /></Field>
          <Field label="Marketing Type"><Select value={form.marketing_type} onChange={set("marketing_type")} options={md.marketing_type} testId="cand-input-marketing_type" /></Field>
          <Field label="Placement Field"><Select value={form.placement_field} onChange={set("placement_field")} options={md.placement_field} testId="cand-input-placement_field" /></Field>
          <Field label="Source of Hire"><Select value={form.source_of_hire} onChange={set("source_of_hire")} options={md.source_of_hire} /></Field>
        </Section>

        <div>
          <label className="text-xs text-slate-500">Evaluation Notes</label>
          <textarea rows={2} value={form.evaluation_notes}
                    onChange={(e) => set("evaluation_notes")(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
        </div>

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="submit" data-testid="cand-submit" disabled={busy}
                  className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white">
            {busy ? "Creating…" : "Create Candidate"}
          </button>
        </div>
      </form>
    </div>
  );
}
